import React, { useEffect, useState } from "react";
import { PencilSimple, CheckCircle } from "@phosphor-icons/react";

// Signature capture — TYPED with an explicit "Adopt & use" step (DocuSign
// style). The signer types their name, picks a signature style, then adopts
// it once — the adopted signature is applied to every signature field in the
// document. Emits { kind: "typed", text, font } only once adopted, else null.
const FONTS = [
  { key: "great_vibes", label: "Great Vibes", css: "'Great Vibes', cursive" },
  { key: "sacramento", label: "Sacramento", css: "'Sacramento', cursive" },
  { key: "pacifico", label: "Pacifico", css: "'Pacifico', cursive" },
];

export const SignaturePad = ({ defaultName = "", onChange, signatureCount = 1 }) => {
  const [name, setName] = useState(defaultName || "");
  const [font, setFont] = useState(FONTS[0].key);
  const [adopted, setAdopted] = useState(false);

  const activeCss = FONTS.find((f) => f.key === font)?.css || FONTS[0].css;

  useEffect(() => {
    const trimmed = (name || "").trim();
    onChange?.(adopted && trimmed ? { kind: "typed", text: trimmed, font } : null);
  }, [adopted, name, font, onChange]);

  if (adopted) {
    return (
      <div data-testid="signature-pad">
        <div className="rounded-md border-2 border-emerald-300 bg-emerald-50/50 px-3 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span
              className="block truncate text-slate-900 leading-none"
              style={{ fontFamily: activeCss, fontSize: "40px" }}
              data-testid="sig-adopted-text"
            >
              {name.trim()}
            </span>
            <span className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700">
              <CheckCircle weight="fill" className="w-3.5 h-3.5" />
              Signature adopted{signatureCount > 1 ? ` — applied to all ${signatureCount} places` : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setAdopted(false)}
            className="shrink-0 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
            data-testid="sig-change"
          >
            <PencilSimple className="w-3.5 h-3.5" /> Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="signature-pad">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Type your full name"
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
        data-testid="sig-type-input"
        autoComplete="name"
      />

      <div className="mt-2 flex flex-wrap gap-2" data-testid="sig-font-picker">
        {FONTS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFont(f.key)}
            className={`flex-1 min-w-[90px] rounded-md border px-2 py-1.5 transition-colors ${
              font === f.key
                ? "border-cyan-500 bg-cyan-50 ring-1 ring-cyan-500/30"
                : "border-slate-200 hover:border-slate-300"
            }`}
            style={{ fontFamily: f.css }}
            data-testid={`sig-font-${f.key}`}
            aria-pressed={font === f.key}
          >
            <span className="text-2xl text-slate-900 leading-none">
              {(name.trim().split(" ")[0] || "Abc")}
            </span>
          </button>
        ))}
      </div>

      <div
        className="mt-2 h-24 w-full border border-slate-200 rounded-md bg-white flex items-center justify-center overflow-hidden px-3"
        data-testid="sig-preview"
      >
        {name.trim() ? (
          <span
            className="text-slate-900 leading-none text-center"
            style={{ fontFamily: activeCss, fontSize: "44px" }}
            data-testid="sig-preview-text"
          >
            {name.trim()}
          </span>
        ) : (
          <span className="text-xs text-slate-400">Your signature will appear here</span>
        )}
      </div>

      <button
        type="button"
        disabled={!name.trim()}
        onClick={() => setAdopted(true)}
        className="mt-2 w-full rounded-md bg-slate-900 text-white text-sm font-medium py-2 disabled:opacity-40 hover:bg-slate-800 transition-colors"
        data-testid="sig-adopt"
      >
        Adopt &amp; use{signatureCount > 1 ? " everywhere" : ""}
      </button>
      <p className="text-[11px] text-slate-400 mt-1.5">
        Type your name, choose a style, then adopt it — this becomes your electronic signature
        {signatureCount > 1 ? ` on all ${signatureCount} signature spots` : ""} in this document.
      </p>
    </div>
  );
};

export default SignaturePad;
